import { useMemo } from "react";
import { useGetUILabelsQuery } from "../services/uiLabel.service";
import { getDefaultLabel } from "../ui-labels";

/**
 * Hook to get UI label with company override support
 * 
 * Priority order:
 * 1. Custom value for the specific key (e.g., "project.location")
 * 2. Custom value for common key (e.g., "common.location") - if label name matches
 * 3. Default value for the specific key
 * 
 * @param key - Label key in format "module.key" (e.g., "common.location", "project.location")
 * @returns The label value with inheritance from common labels
 * 
 * @example
 * ```tsx
 * const locationLabel = useUILabel("common.location");
 * const projectLocationLabel = useUILabel("project.location"); // Will inherit from common.location if not set
 * ```
 */
export function useUILabel(key: string): string {
  const { data: companyLabels, isLoading } = useGetUILabelsQuery(undefined, {
    skip: false,
  });

  return useMemo(() => {
    // Extract module and label name from key (e.g., "project.location" -> module="project", labelName="location")
    const [module, labelName] = key.split(".");
    
    if (!module || !labelName) {
      return getDefaultLabel(key);
    }

    // Priority 1: Check if there's a custom value for this specific key
    if (companyLabels?.labels && companyLabels.labels[key]) {
      const customLabel = companyLabels.labels[key];
      if (customLabel.trim()) {
        return customLabel.trim();
      }
    }

    // Priority 2: If this is not a common label, check if common.labelName exists
    if (module !== "common") {
      const commonKey = `common.${labelName}`;
      if (companyLabels?.labels && companyLabels.labels[commonKey]) {
        const commonLabel = companyLabels.labels[commonKey];
        if (commonLabel.trim()) {
          return commonLabel.trim();
        }
      }
    }

    // Priority 3: Fallback to default label
    return getDefaultLabel(key);
  }, [key, companyLabels?.labels]);
}

/**
 * Hook to get multiple UI labels at once
 * 
 * @param keys - Array of label keys
 * @returns Object with label keys as keys and label values as values
 * 
 * @example
 * ```tsx
 * const labels = useUILabels(["common.location", "common.locationAddress"]);
 * // { "common.location": "Location", "common.locationAddress": "Location Address" }
 * ```
 */
export function useUILabels(keys: string[]): Record<string, string> {
  const { data: companyLabels } = useGetUILabelsQuery(undefined, {
    skip: false,
  });

  return useMemo(() => {
    const result: Record<string, string> = {};
    
    keys.forEach((key) => {
      // Extract module and label name
      const [module, labelName] = key.split(".");
      
      if (!module || !labelName) {
        result[key] = getDefaultLabel(key);
        return;
      }

      // Priority 1: Check specific key
      if (companyLabels?.labels && companyLabels.labels[key]) {
        const customLabel = companyLabels.labels[key];
        if (customLabel.trim()) {
          result[key] = customLabel.trim();
          return;
        }
      }

      // Priority 2: Check common key if not common module
      if (module !== "common") {
        const commonKey = `common.${labelName}`;
        if (companyLabels?.labels && companyLabels.labels[commonKey]) {
          const commonLabel = companyLabels.labels[commonKey];
          if (commonLabel.trim()) {
            result[key] = commonLabel.trim();
            return;
          }
        }
      }

      // Priority 3: Default
      result[key] = getDefaultLabel(key);
    });

    return result;
  }, [keys, companyLabels?.labels]);
}

